
import { useReleases } from "@/hooks/useReleases";
import { ReleasesSearchBar } from "@/components/releases/ReleasesSearchBar";
import { ReleaseCard } from "@/components/releases/ReleaseCard";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/hooks/useLanguage";
import { Skeleton } from "@/components/ui/skeleton";
import { Helmet } from 'react-helmet';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { useEffect } from "react";

export default function Releases() {
  const { t } = useLanguage();
  const { 
    releases, 
    isLoading, 
    error, 
    searchQuery, 
    handleSearchChange, 
    clearSearch,
    totalReleases,
    filteredCount,
    currentPage,
    goToNextPage,
    goToPrevPage,
    hasNextPage,
    hasPrevPage,
    itemsPerPage
  } = useReleases();

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{t('releases.pageTitle')} | Cybermonit</title>
      </Helmet>
      <Navbar />
      <main className="flex-1 container mx-auto px-4 py-24 md:py-32">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('releases.title')}
            </h1>
            <p className="text-muted-foreground mb-6">
              {t('releases.description')}
            </p>
            
            <ReleasesSearchBar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={clearSearch}
              isLoading={isLoading}
              totalReleases={totalReleases}
              filteredCount={filteredCount}
            />
          </div>

          {error ? (
            <div className="text-center py-10">
              <p className="text-destructive mb-2">{t('releases.error_title')}</p>
              <p className="text-muted-foreground">{t('releases.error_description')}</p>
            </div>
          ) : isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(itemsPerPage)].map((_, i) => (
                <div key={i} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-[150px]" />
                      <Skeleton className="h-3 w-[100px]" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-[120px] mt-4" />
                </div>
              ))}
            </div>
          ) : releases.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {releases.map((release, index) => (
                  <ReleaseCard key={`${release.nazwa_projektu}-${release.wersja}-${index}`} release={release} />
                ))}
              </div>
              
              {/* Pagination controls */}
              {totalReleases > itemsPerPage && (
                <Pagination className="mt-8">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={goToPrevPage} 
                        className={!hasPrevPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    <PaginationItem className="flex items-center justify-center mx-2">
                      <span className="text-sm">
                        {currentPage} / {Math.ceil(
                          searchQuery.length >= 3 
                            ? filteredCount / itemsPerPage 
                            : totalReleases / itemsPerPage
                        )}
                      </span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext 
                        onClick={goToNextPage} 
                        className={!hasNextPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          ) : (
            <div className="text-center py-10">
              <p className="text-muted-foreground">{t('releases.no_data')}</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
